<script setup lang="ts">
import { useAppOptionStore } from "@/stores/app-option";
import { useAppVariableStore, generateVariables } from "@/stores/app-variable";
import { onMounted, computed } from "vue";
import * as bootstrap from "bootstrap";
import useEmitter from "@/composables/useEmitter";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const appOption = useAppOptionStore();
const appVariable = useAppVariableStore();
const emitter = useEmitter();
const route = useRoute();
// se determina si se esta en el login o no para cargar unas opciones u otras
const isLoginRoute = computed(() => route.fullPath === "/login");
const isLandingRoute = computed(() => route.fullPath === "/inicio");
// ocultación del componente en las rutas en donde no es deseado
// en todos los casos
const alwaysHidden = ["/kiosk-login", "/kiosk"];
// en el caso de estar en formato móvil
const mobileHidden = [
  "/agenda",
  "/fichajes",
  "/contactos",
  "/gastos",
  "/mensajeria",
];

// computed para detectar si el componente debe ocultarse
const shouldHideComponent = computed(
  () =>
    // oculta si está en alwaysHidden, o si es móvil Y está en mobileHidden
    alwaysHidden.includes(route.fullPath) ||
    (isMobile && mobileHidden.includes(route.fullPath)),
);
// se determina si estamos en vista movil o no
const isMobile = /Mobi|Android/i.test(navigator.userAgent);

const themeList = [
  { name: "Red", bgClass: "bg-red", themeClass: "theme-red" },
  { name: "Pink", bgClass: "bg-pink", themeClass: "theme-pink" },
  { name: "Orange", bgClass: "bg-warning", themeClass: "theme-warning" },
  { name: "Yellow", bgClass: "bg-yellow", themeClass: "theme-yellow" },
  { name: "Lime", bgClass: "bg-lime", themeClass: "theme-lime" },
  { name: "Green", bgClass: "bg-green", themeClass: "theme-green" },
  { name: "Default", bgClass: "bg-teal", themeClass: "theme-teal" },
  { name: "Cyan", bgClass: "bg-info", themeClass: "theme-info" },
  { name: "Blue", bgClass: "bg-primary", themeClass: "theme-primary" },
  { name: "Purple", bgClass: "bg-purple", themeClass: "theme-purple" },
  { name: "Indigo", bgClass: "bg-indigo", themeClass: "theme-indigo" },
  { name: "Black", bgClass: "bg-black", themeClass: "theme-gray-600" },
];

function reloadVariable() {
  const newVariables = generateVariables();
  Object.assign(appVariable.font, newVariables.font);
  Object.assign(appVariable.color, newVariables.color);
}

function appThemePanelToggled(event: Event) {
  event.preventDefault();
  appOption.appThemePanelToggled = !appOption.appThemePanelToggled;
  localStorage.setItem(
    "appThemePanelToggled",
    JSON.stringify(appOption.appThemePanelToggled),
  );
}

function toggleTheme(event: Event, themeClass: string) {
  event.preventDefault();
  appOption.appThemeClass = themeClass;
  localStorage.setItem("appThemeClass", themeClass);
  setThemeClass(themeClass);
}

function setThemeClass(themeClass: string) {
  document.body.className = document.body.className
    .replace(/theme-\S+/g, "")
    .trim();
  document.body.classList.add(themeClass);
  reloadVariable();
  emitter.emit("theme-reload", true);
}

function toggleDarkMode() {
  appOption.appDarkMode = !appOption.appDarkMode;
  localStorage.setItem("appDarkMode", JSON.stringify(appOption.appDarkMode));
  setDarkMode(appOption.appDarkMode);
}

function setDarkMode(darkMode: boolean) {
  document
    .querySelector("html")
    ?.setAttribute("data-bs-theme", darkMode ? "dark" : "light");
  reloadVariable();
  emitter.emit("theme-reload", true);
}

function toggleHeaderFixed() {
  if (!appOption.appHeaderFixed && appOption.appSidebarFixed) {
    appOption.appSidebarFixed = false;
    localStorage.appSidebarFixed = appOption.appSidebarFixed;
    alert(
      "La opción de cabecera flotante con barra lateral fija no está soportada. Si la cabecera es flotante la barra lateral también debe serlo.",
    );
  }
  if (localStorage) {
    localStorage.appHeaderFixed = appOption.appHeaderFixed;
  }
}

function toggleHeaderInverse() {
  if (localStorage) {
    localStorage.appHeaderInverse = appOption.appHeaderInverse;
  }
}

function toggleSidebarFixed() {
  if (!appOption.appHeaderFixed && appOption.appSidebarFixed) {
    appOption.appHeaderFixed = true;
    localStorage.appHeaderFixed = appOption.appHeaderFixed;
    alert(
      "La opción barra lateral fija con cabecera flotante no está soportada. Si la barra lateral es fija la cebecera también debe serlo.",
    );
  }
  if (localStorage) {
    localStorage.appSidebarFixed = appOption.appSidebarFixed;
  }
}

function toggleSidebarGrid() {
  if (localStorage) {
    localStorage.appSidebarGrid = appOption.appSidebarGrid;
  }
}

function toggleGradientEnabled() {
  if (localStorage) {
    localStorage.appGradientEnabled = appOption.appGradientEnabled;
  }
}

function toggleTransparentEnabled() {
  if (localStorage) {
    localStorage.appTransparentEnabled = appOption.appSidebarTransparent;
  }
}

function toggleSidebarEndEnabled() {
  if (localStorage) {
    localStorage.appSidebarEndEnabled = appOption.appSidebarEnd;
  }
}

function toggleSidebarLightEnabled() {
  if (localStorage) {
    localStorage.appSidebarLightEnabled = appOption.appSidebarLight;
  }
}

function toggleTopMenuEnabled() {
  if (localStorage) {
    localStorage.appTopMenuEnabled = appOption.appTopMenu;
    appOption.appSidebarHide = !appOption.appSidebarHide;
  }
}

onMounted(() => {
  const tooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltips.forEach((el) => new (bootstrap as any).Tooltip(el));

  const settings = [
    "appThemePanelToggled",
    "appThemeClass",
    "appDarkMode",
    "appHeaderInverse",
    "appHeaderFixed",
    "appSidebarFixed",
    "appSidebarGrid",
    "appGradientEnabled",
    "appSidebarTransparent",
    "appSidebarEnd",
    "appSidebarLight",
    "appTopMenu",
  ];

  settings.forEach((key) => {
    try {
      const item = localStorage.getItem(key);

      if (item !== null && item.trim() !== "") {
        let value;
        try {
          value = JSON.parse(item);
        } catch {
          value = item;
        }
        (appOption as any)[key] = value;
      }
    } catch (error) {
      console.error(
        `⚠ Error al parsear el valor de localStorage para la clave "${key}"`,
        error,
      );
    }
  });

  if (appOption.appThemeClass) {
    setThemeClass(appOption.appThemeClass);
  }
  if (appOption.appDarkMode !== undefined) {
    setDarkMode(appOption.appDarkMode);
  }
});

// reseteo de opciones a su estado por defecto, se deja tanto el navBar como el sideBar fijos
const resetSettings = () => {
  appOption.appDarkMode = false;
  localStorage.removeItem("appDarkMode");
  setDarkMode(localStorage.appDarkMode);
  appOption.appHeaderInverse = false;
  localStorage.removeItem("appHeaderInverse");
  appOption.appHeaderFixed = true;
  localStorage.removeItem("appHeaderFixed");
  appOption.appSidebarFixed = true;
  localStorage.removeItem("appSidebarFixed");
  appOption.appSidebarGrid = false;
  localStorage.removeItem("appSidebarGrid");
  appOption.appGradientEnabled = false;
  localStorage.removeItem("appGradientEnabled");
  appOption.appSidebarTransparent = false;
  localStorage.removeItem("appTransparentEnabled");
  appOption.appSidebarEnd = false;
  localStorage.removeItem("appSidebarEndEnabled");
  appOption.appSidebarLight = false;
  localStorage.removeItem("appSidebarLightEnabled");
  appOption.appTopMenu = false;
  appOption.appSidebarHide = false;
  localStorage.removeItem("appTopMenuEnabled");
  // reseteo a color por defecto
  appOption.appThemeClass = "bg-teal";
  localStorage.removeItem("appThemeClass");
  // se añade el atributo "active" para el check del botón del color
  addClassActive();
  // se busca el botón deseado y se le hace click
  const colorActive = document.getElementsByClassName(
    "theme-list-link bg-teal",
  );
  if (colorActive.length > 0) {
    // se selecciona el primer elemento de la colección y se simula un click
    (colorActive[0] as HTMLElement).click();
  }
};
const addClassActive = () => {
  // Seleccionar todos los elementos con la clase "theme-list-item"
  const elementos = document.querySelectorAll(".theme-list-item");
  // Verificar si se encontraron elementos
  if (elementos.length > 0) {
    // Seleccionar el elemento requerido, en este caso el 6
    var elemento = elementos[6];
    // Agregar la clase "active" al elemento seleccionado
    elemento.classList.add("active");
  } else {
    console.log("No se encontraron elementos con la clase 'theme-list-item'");
  }
};
</script>
<template>
  <div
    class="app-theme-panel d-none"
    v-bind:class="{ active: appOption.appThemePanelToggled === true }"
  >
    <div class="app-theme-panel-container">
      <a href="#" v-on:click="appThemePanelToggled" class="app-theme-toggle-btn"
        ><i class="bi bi-sliders"></i
      ></a>
      <div class="app-theme-panel-content">
        <div class="small fw-bold text-white mb-1">Theme Color</div>
        <div class="mb-3">
          <div class="p-2">
            <div class="app-theme-list">
              <div
                class="app-theme-list-item"
                v-bind:class="{
                  active:
                    appOption.appThemeClass == theme.themeClass ||
                    (!appOption.appThemeClass && theme.name == 'Default'),
                }"
                v-for="theme in themeList"
              >
                <a
                  href="javascript:;"
                  class="app-theme-list-link"
                  v-bind:class="theme.bgClass"
                  v-on:click="(event) => toggleTheme(event, theme.themeClass)"
                  data-bs-toggle="tooltip"
                  data-bs-trigger="hover"
                  data-bs-container="body"
                  v-bind:data-bs-title="theme.name"
                  >&nbsp;</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- theme-panel -->
  <div
    v-if="!shouldHideComponent"
    class="theme-panel"
    v-bind:class="{ active: appOption.appThemePanelToggled }"
  >
    <a
      href="javascript:;"
      v-on:click="appThemePanelToggled"
      class="theme-collapse-btn"
      ><i class="fa fa-cog"></i
    ></a>
    <perfect-scrollbar class="theme-panel-content h-100">
      <h5>{{ t("themePanel.colorSettings") }}</h5>

      <!-- theme-list -->
      <div class="theme-list">
        <div
          class="theme-list-item"
          v-bind:class="{
            active:
              appOption.appThemeClass == theme.themeClass ||
              (!appOption.appThemeClass && theme.name == 'Default'),
          }"
          v-for="theme in themeList"
        >
          <a
            href="javascript:;"
            class="theme-list-link"
            v-bind:class="theme.bgClass"
            v-on:click="(event) => toggleTheme(event, theme.themeClass)"
            data-bs-toggle="tooltip"
            data-bs-trigger="hover"
            data-bs-container="body"
            v-bind:data-bs-title="theme.name"
            >&nbsp;</a
          >
        </div>
      </div>
      <!-- / theme-list -->

      <div class="theme-panel-divider"></div>

      <!-- dark mode -->
      <div class="row mt-10px">
        <div class="col-8 control-label text-dark fw-bold">
          <div>
            {{ t("themePanel.darkMode") }}
            <!-- <span class="badge bg-primary ms-1 py-2px position-relative" style="top: -1px;">NEW</span> -->
          </div>
          <!-- <div class="lh-14">
						<small class="text-dark opacity-50">
							Adjust the appearance to reduce glare and give your eyes a break.
						</small>
					</div> -->
        </div>
        <div class="col-4 d-flex">
          <div class="form-check form-switch ms-auto mb-0">
            <input
              type="checkbox"
              class="form-check-input"
              v-on:change="toggleDarkMode()"
            />
            <label class="form-check-label" for="appThemeDarkMode"
              >&nbsp;</label
            >
          </div>
        </div>
      </div>
      <!-- / dark mode -->
      <div v-if="!(isLoginRoute || (isLandingRoute && isMobile))">
        <div class="theme-panel-divider"></div>

        <!-- header-switches -->
        <div class="row mt-10px align-items-center">
          <div class="col-8 control-label text-dark fw-bold">
            {{ t("themePanel.headerFixed") }}
          </div>
          <div class="col-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appHeaderFixed"
                v-on:change="toggleHeaderFixed()"
                v-model="appOption.appHeaderFixed"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appHeaderFixed"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-8 control-label text-dark fw-bold">
            {{ t("themePanel.headerInverse") }}
          </div>
          <div class="col-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appHeaderInverse"
                v-on:change="toggleHeaderInverse()"
                v-model="appOption.appHeaderInverse"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appHeaderInverse"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <!--/ header-switches -->

        <div class="theme-panel-divider"></div>

        <!-- sidebar-switches -->
        <div class="row mt-10px align-items-center">
          <div class="col-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarFixed") }}
          </div>
          <div class="col-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appSidebarFixed"
                v-on:change="toggleSidebarFixed()"
                v-model="appOption.appSidebarFixed"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appSidebarFixed"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarGrid") }}
          </div>
          <div class="col-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appSidebarGrid"
                v-on:change="toggleSidebarGrid()"
                v-model="appOption.appSidebarGrid"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appSidebarGrid"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-md-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarGradient") }}
          </div>
          <div class="col-md-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appGradientEnabled"
                v-on:change="toggleGradientEnabled()"
                v-model="appOption.appGradientEnabled"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appGradientEnabled"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-md-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarTransparent") }}
          </div>
          <div class="col-md-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appGradientEnabled"
                v-on:change="toggleTransparentEnabled()"
                v-model="appOption.appSidebarTransparent"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appGradientEnabled"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-md-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarRight") }}
          </div>
          <div class="col-md-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appGradientEnabled"
                v-on:change="toggleSidebarEndEnabled()"
                v-model="appOption.appSidebarEnd"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appGradientEnabled"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-md-8 control-label text-dark fw-bold">
            {{ t("themePanel.sidebarLight") }}
          </div>
          <div class="col-md-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appGradientEnabled"
                v-on:change="toggleSidebarLightEnabled()"
                v-model="appOption.appSidebarLight"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appGradientEnabled"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <div class="row mt-10px align-items-center">
          <div class="col-md-8 control-label text-dark fw-bold">
            {{ t("themePanel.topMenu") }}
          </div>
          <div class="col-md-4 d-flex">
            <div class="form-check form-switch ms-auto mb-0">
              <input
                type="checkbox"
                class="form-check-input"
                id="appGradientEnabled"
                v-on:change="toggleTopMenuEnabled()"
                v-model="appOption.appTopMenu"
                true-value="true"
                false-value=""
              />
              <label class="form-check-label" for="appGradientEnabled"
                >&nbsp;</label
              >
            </div>
          </div>
        </div>
        <!-- / sidebar-switches -->

        <div class="theme-panel-divider"></div>

        <a
          href="javascript:;"
          @click="resetSettings"
          class="btn btn-default d-block w-100 rounded-pill"
          data-toggle="reset-local-storage"
          ><b>{{ t("themePanel.resetSettings") }}</b></a
        >
      </div>
      <div class="theme-panel-divider"></div>

      <div class="text-gray-600 text-center text-gray-500-darker mb-0">
        &copy; Sofycom 2025
      </div>
    </perfect-scrollbar>
  </div>
  <!-- / theme-panel -->

  <!-- <h5>Admin Design (5)</h5> -->
  <!-- BEGIN theme-version -->
  <!-- <div class="theme-version">
				<div class="theme-version-item">
					<a href="../template_html/index_v2.html" class="theme-version-link active">
						<span style="background-image: url(../assets/img/theme/default.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_transparent/index_v2.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/transparent.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_apple/index_v2.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/apple.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_material/index_v2.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/material.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_facebook/index_v2.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/facebook.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_google/index_v2.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/google.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
			</div> -->
  <!-- END theme-version -->

  <!-- <div class="theme-panel-divider"></div> -->

  <!-- <h5>Language Version (7)</h5> -->
  <!-- BEGIN theme-version -->
  <!-- <div class="theme-version">
				<div class="theme-version-item">
					<a href="../template_html/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/html.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_ajax/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/ajax.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_angularjs/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/angular1x.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_angularjs17/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/angular10x.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="javascript:alert('Laravel 10 Version only available in downloaded version.');" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/laravel.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_vue3/index.html" class="theme-version-link active">
						<span style="background-image: url(../assets/img/version/vuejs.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../template_reactjs/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/reactjs.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="javascript:alert('.NET Core 7.0 MVC Version only available in downloaded version.');" class="theme-version-link">
						<span style="background-image: url(../assets/img/version/dotnet.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
			</div> -->
  <!-- END theme-version -->

  <!-- <div class="theme-panel-divider"></div> -->

  <!-- <h5>Frontend Design (5)</h5> -->
  <!-- BEGIN theme-version -->
  <!-- <div class="theme-version">
				<div class="theme-version-item">
					<a href="../../../frontend/template/template_one_page_parallax/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/one-page-parallax.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../../../frontend/template/template_e_commerce/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/e-commerce.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../../../frontend/template/template_blog/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/blog.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../../../frontend/template/template_forum/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/forum.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
				<div class="theme-version-item">
					<a href="../../../frontend/template/template_corporate/index.html" class="theme-version-link">
						<span style="background-image: url(../assets/img/theme/corporate.jpg);" class="theme-version-cover"></span>
					</a>
				</div>
			</div> -->
  <!-- END theme-version -->

  <!-- <div class="theme-panel-divider"></div> -->
</template>
